import { BASE_YEAR } from './finance-constants';

/* eslint-disable no-magic-numbers */
export const EST_OFFSET = 300;
export const EDT_OFFSET = 240;
const MS_IN_MIN = 60000;

/**
 * Given a date in the host systems arbitrary timezone, returns a date object
 * that behaves like it is in the eastern timezone (e.g. will shift the date object
 * forward three hours to go from Pacific to Eastern time)
 * @param {Date}{Number} date The "accurate" date in the user's arbitrary timezone, or epoch time if a number
 * @returns {Date} the date moved to eastern time.  If before 1970, just returns the date unchanged
 *
 */
export function getEasternTimeFromDate(date) {
	if (!date) return undefined;
	let realDate = new Date(date); //ensure we have a date object and not just a number
	let offset = getTimezoneDiffFromEastern(realDate);
	if (!offset) return realDate;
	return new Date(realDate.getTime() + offset*MS_IN_MIN);
}

/**
 * Given a date that has been converted to eastern time from the hosts timezone, convert it back
 * to the host systems arbitrary timezone (i.e. the inverse of getEasternTimeFromDate()
 * @param {Date}{Number} date The pretending to be Eastern Time date based on the user's arbitrary timezone
 * @returns {Date} the date moved to the users local time.  If before 1970, just returns the date unchanged
 *
 */
export function getLocalTimeFromEasternDate(date) {
	if (!date) return undefined;
	let realDate = new Date(date); //ensure we have a date object and not just a number
	let offset = getTimezoneDiffFromEastern(realDate);
	if (!offset) return realDate;
	return new Date(realDate.getTime() - offset*MS_IN_MIN);
}

/**
 * Given a date in local time, determine wat the timezone offset difference is for that date
 * to eastern time
 */
function getTimezoneDiffFromEastern(date) {
	let time = date.getTime();
	let year = date.getFullYear();
	//around Jan 1st, year might not match the equivalent year for Eastern time
	//but we default to EST, so it's OK.

	//if we don't have DST data for the year in question, just base it off of EST as a fallback
	if (year < BASE_YEAR) return date.getTimezoneOffset() - EST_OFFSET;
	let easternOffset = time > DST[year][0] && time <= DST[year][1] ? EDT_OFFSET: EST_OFFSET;
	return date.getTimezoneOffset() - easternOffset;
}

export function isSameDateEST(date, prevDate) {
	let dateEST = getEasternTimeFromDate(new Date(date));
	let prevDateEST =  getEasternTimeFromDate(new Date(prevDate));
	let isSameDateEST = dateEST.getDate() !== prevDateEST.getDate();

	return isSameDateEST;
}

/* You can generate the following array in a browser/node environment running in Eastern Time with the following code:
	for(let i = 1962; i<=2030; i++) {
		let time = new Date(i, 0, 1).getTime();
		while (new Date(time).getTimezoneOffset() !== 240) { time += 1000; };
		startDst = time;
		while (new Date(time).getTimezoneOffset() !== 300) { time += 1000; }
		endDst = time;
		console.log(`[${i}, ${startDst/100000}, ${endDst/100000}],`);
	}
*/

//to save bites, remove the trailing 00000 from each millisecond timestamp
export const DST = [
	[1962, -2464740, -2259144],
	[1963, -2150244, -1944648],
	[1964, -1835748, -1630152],
	[1965, -1515204, -1309608],
	[1966, -1200708, -995112],
	[1967, -886212, -680616],
	[1968, -571716, -366120],
	[1969, -257220, -51624],
	[1970, 99612, 256824],
	[1971, 414108, 577368],
	[1972, 734652, 891864],
	[1973, 1049148, 1206360],
	[1974, 1266876, 1520856],
	[1975, 1623708, 1835352],
	[1976, 1992636, 2155896],
	[1977, 2307132, 2470392],
	[1978, 2627676, 2784888],
	[1979, 2942172, 3099384],
	[1980, 3256668, 3413880],
	[1981, 3571164, 3728376],
	[1982, 3885660, 4048920],
	[1983, 4200156, 4363416],
	[1984, 4520700, 4677912],
	[1985, 4835196, 4992408],
	[1986, 5149692, 5306904],
	[1987, 5446044, 5621400],
	[1988, 5760540, 5941944],
	[1989, 6075036, 6256440],
	[1990, 6389532, 6570936],
	[1991, 6710076, 6885432],
	[1992, 7024572, 7199928],
	[1993, 7339068, 7520472],
	[1994, 7653564, 7834968],
	[1995, 7968060, 8149464],
	[1996, 8288604, 8463960],
	[1997, 8603100, 8778456],
	[1998, 8917596, 9092952],
	[1999, 9232092, 9413496],
	[2000, 9546588, 9727992],
	[2001, 9861084, 10042488],
	[2002, 10181628, 10356984],
	[2003, 10496124, 10671480],
	[2004, 10810620, 10992024],
	[2005, 11125116, 11306520],
	[2006, 11439612, 11621016],
	[2007, 11735964, 11941560],
	[2008, 12050460, 12256056],
	[2009, 12364956, 12570552],
	[2010, 12685500, 12891096],
	[2011, 12999996, 13205592],
	[2012, 13314492, 13520088],
	[2013, 13628988, 13834584],
	[2014, 13943484, 14149080],
	[2015, 14257980, 14463576],
	[2016, 14578524, 14784120],
	[2017, 14893020, 15098616],
	[2018, 15207516, 15413112],
	[2019, 15522012, 15727608],
	[2020, 15836508, 16042104],
	[2021, 16157052, 16362648],
	[2022, 16471548, 16677144],
	[2023, 16786044, 16991640],
	[2024, 17100540, 17306136],
	[2025, 17415036, 17620632],
	[2026, 17729532, 17935128],
	[2027, 18050076, 18255672],
	[2028, 18364572, 18570168],
	[2029, 18679068, 18884664],
	[2030, 18993564, 19199160]]
	.reduce((acc, [year,...times]) => {acc[year] = times.map((v) => v*100000); return acc;}, {});
